<div class="tdbf-page">
  <div class="tdbf-save-bar">
    <div class="tdbf-save-bar__inner">
      <div class="tdbf-save-bar__left">
        <button
          type="button"
          class="tdbf-save-bar__back"
          [attr.aria-label]="'testdatenblaetter.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tdbf-save-bar__heading">
          <span class="tdbf-save-bar__eyebrow">
            @if (isEdit) {
              {{ 'testdatenblaetter.form.eyebrowExisting' | translate: { id: tdbLabel() } }}
            } @else {
              {{ 'testdatenblaetter.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="tdbf-save-bar__title">
            {{ (isEdit ? 'testdatenblaetter.form.editTitle' : 'testdatenblaetter.form.createTitle') | translate }}
          </span>
        </div>
        @if (form.dirty) {
          <span class="tdbf-dirty-pill" data-testid="tdb-dirty-badge">
            <span class="tdbf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testdatenblaetter.form.dirty' | translate }}
          </span>
        }
      </div>
      <div class="tdbf-save-bar__right">
        @if (!isReadOnly()) {
          <button
            mat-stroked-button
            type="button"
            class="tdbf-csv-btn"
            data-testid="tdb-csv-btn"
            disabled
            disabledInteractive
            [matTooltip]="'testdatenblaetter.form.importCsvUnavailable' | translate"
          >
            <mat-icon aria-hidden="true">upload</mat-icon>
            {{ 'testdatenblaetter.form.importCsv' | translate }}
          </button>
        }
        <button mat-stroked-button type="button" (click)="onCancel()">
          {{ 'testdatenblaetter.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="tdb-save-btn"
            [disabled]="saveDisabled()"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testdatenblaetter.form.save' : 'testdatenblaetter.form.saveNew') | translate }}
          </button>
        }
      </div>
    </div>
  </div>

  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="tdbf-archiviert-hinweis" />
  } @else if (isProjectReader()) {
    <div class="tdbf-readonly-hint">
      <mat-icon class="tdbf-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="tdbf-readonly-hint__text">{{ 'testdatenblaetter.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="tdbf-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (notFound()) {
    <div class="tdbf-page__not-found">
      <p role="alert" data-testid="tdb-not-found">{{ 'testdatenblaetter.form.notFound' | translate }}</p>
      <a
        class="tdbf-page__back-link"
        data-testid="tdb-back-to-overview"
        [routerLink]="['/projects', projectId, 'testdatenblaetter']"
      >
        {{ 'testdatenblaetter.form.backToOverview' | translate }}
      </a>
    </div>
  } @else if (loadError()) {
    <p class="tdbf-page__error" role="alert" data-testid="tdb-load-error">
      {{ 'testdatenblaetter.form.loadError' | translate }}
    </p>
  } @else {
    <div class="tdbf-body" [formGroup]="form">
      <div class="tdbf-main" data-testid="tdb-main">
        @if (saveError()) {
          <p class="tdbf-hint tdbf-hint--error" role="alert" aria-live="polite" data-testid="tdb-save-error">
            @if (saveNotFound()) {
              {{ 'testdatenblaetter.form.saveNotFound' | translate }}
            } @else {
              {{ saveErrorText() ?? ('testdatenblaetter.form.saveError' | translate) }}
            }
          </p>
        }

        <!-- Grunddaten -->
        <div class="tdbf-card" data-testid="tdb-basics">
          <div class="tdbf-card__body">
            <to-section-title
              [title]="'testdatenblaetter.form.section.basics' | translate"
              [subtitle]="'testdatenblaetter.form.section.basicsSubtitle' | translate"
            />
            <div class="tdbf-fields">
              <mat-form-field appearance="outline" floatLabel="always" class="tdbf-field tdbf-field--uppercase-label">
                <mat-label>{{ 'testdatenblaetter.form.field.name' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="name"
                  data-testid="tdb-name-input"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  [placeholder]="'testdatenblaetter.form.field.namePlaceholder' | translate"
                  (input)="form.controls.name.markAsTouched()"
                />
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.name.value.length }} / {{ fieldLengths.NAME_MAX }}
                </mat-hint>
                @if (form.controls.name.hasError('required') && form.controls.name.touched) {
                  <mat-error>{{ 'testdatenblaetter.form.field.nameRequired' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always" class="tdbf-field tdbf-field--uppercase-label">
                <mat-label>{{ 'testdatenblaetter.form.field.description' | translate }}</mat-label>
                <textarea
                  matInput
                  rows="2"
                  formControlName="beschreibung"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'testdatenblaetter.form.field.descriptionPlaceholder' | translate"
                ></textarea>
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.beschreibung.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                </mat-hint>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Daten (Spreadsheet) -->
        <div class="tdbf-card">
          <div class="tdbf-card__body">
            <div class="tdbf-card__head">
              <div class="tdbf-card__head-text">
                <to-section-title
                  data-testid="tdbf-data-subtitle"
                  [title]="'testdatenblaetter.form.section.data' | translate"
                  [subtitle]="
                    'testdatenblaetter.form.section.dataSubtitle'
                      | translate
                        : {
                            variables: dataVariablesKey() | translate: { count: cols().length },
                            records: dataRecordsKey() | translate: { count: rows().length },
                            active: activeCount(),
                          }
                  "
                />
              </div>
              @if (!isReadOnly()) {
                <div class="tdbf-card__head-actions">
                  <button
                    #addColBtn
                    type="button"
                    class="tdbf-btn tdbf-btn--ghost"
                    data-testid="tdb-add-col"
                    (click)="addCol()"
                  >
                    <mat-icon aria-hidden="true">add</mat-icon>
                    {{ 'testdatenblaetter.form.data.addColumn' | translate }}
                  </button>
                  <button
                    #addRowBtn
                    type="button"
                    class="tdbf-btn tdbf-btn--primary"
                    data-testid="tdb-add-row"
                    (click)="addRow()"
                  >
                    <mat-icon aria-hidden="true">add</mat-icon>
                    {{ 'testdatenblaetter.form.data.addRow' | translate }}
                  </button>
                </div>
              }
            </div>

            <div class="tdbf-grid-wrap">
              <table class="tdbf-grid">
                <thead>
                  <tr>
                    <th class="tdbf-grid__index-head">#</th>
                    <th class="tdbf-grid__active-head">{{ 'testdatenblaetter.form.data.activeHead' | translate }}</th>
                    @for (col of cols(); track $index) {
                      @let blank = isBlankColumnName(col);
                      <th class="tdbf-grid__col-head">
                        <div class="tdbf-grid__col-head-inner">
                          <input
                            class="tdbf-grid__col-input"
                            data-testid="tdb-col-name-input"
                            [class.tdbf-grid__col-input--invalid]="blank"
                            [attr.aria-invalid]="blank ? 'true' : null"
                            [attr.aria-describedby]="blank ? 'tdb-col-name-error-' + $index : null"
                            [value]="col"
                            [disabled]="isReadOnly()"
                            [attr.aria-label]="
                              'testdatenblaetter.form.data.columnNameAria' | translate: { index: $index + 1 }
                            "
                            (input)="renameCol($index, $any($event.target).value)"
                          />
                          @if (!isReadOnly()) {
                            <button
                              type="button"
                              #colDel
                              class="tdbf-grid__col-del"
                              data-testid="tdb-col-del"
                              [disabled]="cols().length <= 1"
                              [attr.aria-label]="'testdatenblaetter.form.data.removeColumn' | translate"
                              (click)="delCol($index)"
                            >
                              <mat-icon aria-hidden="true">close</mat-icon>
                            </button>
                          }
                        </div>
                        @if (blank) {
                          <p
                            class="tdbf-grid__col-error"
                            data-testid="tdb-col-name-error"
                            [id]="'tdb-col-name-error-' + $index"
                          >
                            {{ 'testdatenblaetter.form.data.columnNameRequired' | translate }}
                          </p>
                        }
                      </th>
                    }
                    <th class="tdbf-grid__spacer-head"></th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of rows(); track $index; let ri = $index) {
                    @let on = active()[ri];
                    <tr [class.tdbf-grid__row--inactive]="!on">
                      <td class="tdbf-grid__index-cell">{{ ri + 1 }}</td>
                      <td class="tdbf-grid__active-cell">
                        <button
                          type="button"
                          role="switch"
                          class="tdbf-switch"
                          data-testid="tdb-row-active"
                          [class.tdbf-switch--on]="on"
                          [attr.aria-checked]="on"
                          [attr.aria-label]="'testdatenblaetter.form.data.rowActiveAria' | translate: { row: ri + 1 }"
                          [matTooltip]="on ? '' : ('testdatenblaetter.form.data.rowInactiveHint' | translate)"
                          [disabled]="isReadOnly()"
                          (click)="toggleRowActive(ri)"
                        >
                          <span class="tdbf-switch__thumb" aria-hidden="true"></span>
                        </button>
                      </td>
                      @for (cell of row; track $index; let ci = $index) {
                        @let locked = isEncrypted(cell);
                        @let key = cellKey(ri, ci);
                        <td class="tdbf-grid__cell">
                          <div class="tdbf-grid__cell-inner">
                            <input
                              class="tdbf-grid__cell-input"
                              [class.tdbf-grid__cell-input--locked]="locked"
                              [attr.data-testid]="'tdbf-cell-' + key"
                              autocomplete="off"
                              [value]="locked ? encryptedDisplay : cell"
                              [readOnly]="locked"
                              [disabled]="isReadOnly()"
                              [attr.aria-label]="
                                locked
                                  ? ('testdatenblaetter.form.data.encryptedCell' | translate)
                                  : ('testdatenblaetter.form.data.cellAria' | translate: { row: ri + 1, col: ci + 1 })
                              "
                              (click)="startReentry(ri, ci)"
                              (keydown.enter)="startReentry(ri, ci)"
                              (keydown.escape)="cancelReentry(ri, ci)"
                              (blur)="endReentry(ri, ci)"
                              (input)="setCell(ri, ci, $any($event.target).value)"
                            />
                            @if (!isReadOnly() && !locked) {
                              <button
                                type="button"
                                class="tdbf-grid__cell-encrypt"
                                [attr.data-testid]="'tdbf-cell-encrypt-' + key"
                                [disabled]="!cell || encryptingCell() !== null"
                                [attr.aria-busy]="encryptingCell() === key"
                                [attr.aria-label]="'testdatenblaetter.form.data.encryptCell' | translate"
                                (click)="encryptCell(ri, ci)"
                              >
                                <mat-icon aria-hidden="true">lock</mat-icon>
                              </button>
                            }
                          </div>
                          @if (failedCell() === key) {
                            <p class="tdbf-grid__cell-error" role="alert" [attr.data-testid]="'tdbf-cell-error-' + key">
                              {{ 'secretEncrypt.error' | translate }}
                            </p>
                          }
                        </td>
                      }
                      <td class="tdbf-grid__row-del-cell">
                        @if (!isReadOnly()) {
                          <button
                            type="button"
                            #rowDel
                            class="tdbf-grid__row-del"
                            data-testid="tdb-row-del"
                            [disabled]="rows().length <= 1"
                            [attr.aria-label]="'testdatenblaetter.form.data.removeRow' | translate"
                            (click)="delRow(ri)"
                          >
                            <mat-icon aria-hidden="true">delete</mat-icon>
                          </button>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="tdbf-info">
              <mat-icon class="tdbf-info__icon" aria-hidden="true">content_copy</mat-icon>
              <span>{{ 'testdatenblaetter.form.data.info' | translate: { count: activeCount() } }}</span>
            </div>
          </div>
        </div>

        <app-testdatenblatt-details-historie
          [projectId]="projectId"
          [testdatenblattId]="currentTdbId()"
          [readonly]="isReadOnly()"
          [personOptions]="personOptions()"
          [currentUserId]="currentUserId()"
          [notes]="notesText()"
          [notesControl]="form.controls.notes"
        />
      </div>

      <div class="tdbf-sidebar" data-testid="tdb-sidebar">
        <div class="tdbf-card">
          <div class="tdbf-card__body tdbf-card__body--sidebar">
            <h2 class="tdbf-sidebar__title">{{ 'testdatenblaetter.form.section.properties' | translate }}</h2>
            <div class="tdbf-fields">
              <app-person-select
                class="tdbf-field"
                formControlName="assigneeUserId"
                [label]="'testdatenblaetter.form.field.assignee' | translate"
                [options]="personOptions()"
                [allowNone]="false"
                [required]="true"
                noneKey="testdatenblaetter.form.field.noAssignee"
              ></app-person-select>
              @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
                <p class="tdbf-field-error" role="alert">
                  {{ 'testdatenblaetter.form.field.assigneeRequired' | translate }}
                </p>
              }
              <mat-form-field appearance="outline" floatLabel="always" class="tdbf-field tdbf-field--uppercase-label">
                <mat-label>{{ 'testdatenblaetter.form.field.version' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="version"
                  data-testid="tdb-version-input"
                  [attr.maxlength]="fieldLengths.KEY_MAX"
                  [placeholder]="'testdatenblaetter.form.field.versionPlaceholder' | translate"
                  (input)="form.controls.version.markAsTouched()"
                />
                @if (form.controls.version.hasError('maxlength')) {
                  <mat-error data-testid="tdb-version-error">
                    {{ 'testdatenblaetter.form.field.versionTooLong' | translate: { max: fieldLengths.KEY_MAX } }}
                  </mat-error>
                }
              </mat-form-field>
              <mat-form-field
                appearance="outline"
                floatLabel="always"
                class="tdbf-field tdbf-field--uppercase-label"
                data-testid="tdb-tags"
              >
                <mat-label>{{ 'testdatenblaetter.form.field.tags' | translate }}</mat-label>
                <mat-chip-grid
                  #tagGrid
                  [disabled]="isReadOnly()"
                  [attr.aria-label]="'testdatenblaetter.form.field.tagsAriaLabel' | translate"
                >
                  @for (tag of form.controls.tags.value; track tag) {
                    <mat-chip-row [removable]="!isReadOnly()" (removed)="removeTag(tag)">
                      {{ tag }}
                      @if (!isReadOnly()) {
                        <button
                          matChipRemove
                          data-testid="tdb-tag-remove"
                          [attr.aria-label]="'testdatenblaetter.form.field.tagsRemove' | translate: { tag }"
                        >
                          <mat-icon aria-hidden="true">cancel</mat-icon>
                        </button>
                      }
                    </mat-chip-row>
                  }
                </mat-chip-grid>
                <!-- MatChipGrid needs its input; readers get it disabled and without placeholder. -->
                <input
                  data-testid="tdb-tag-input"
                  [matChipInputFor]="tagGrid"
                  [matChipInputAddOnBlur]="true"
                  [matChipInputSeparatorKeyCodes]="tagSeparatorKeys"
                  [disabled]="isReadOnly()"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  [placeholder]="isReadOnly() ? '' : ('testdatenblaetter.form.field.tagsPlaceholder' | translate)"
                  (matChipInputTokenEnd)="addTag($event)"
                />
              </mat-form-field>
            </div>
          </div>
        </div>
        <app-testdatenblatt-usage [projectId]="projectId" [testdatenblattId]="currentTdbId()" />
      </div>
    </div>
  }
</div>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'testdatenblaetter.form.unsavedMessage' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
